import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUserProfile } from '../../services/userService';
import { getOrderStatus } from '../../services/shopService';

const Orders = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadOrders = async () => {
      if (!currentUser) return;
      try {
        const profile = await getUserProfile(currentUser.uid);
        const profileOrders = profile?.orders || [];
        const ordersArray = Array.isArray(profileOrders) ? profileOrders : [];

        // Fetcha lo stato reale da Firestore per ogni ordine
        const enrichedOrders = await Promise.all(
          ordersArray.map(async (order) => {
            try {
              const realOrder = await getOrderStatus(order.shopId, order.orderId);
              if (realOrder) {
                return {
                  ...order,
                  status: realOrder.status,
                  paymentStatus: realOrder.paymentStatus,
                  paymentMethod: realOrder.paymentMethod,
                  items: realOrder.items,
                  total: realOrder.total,
                  subtotal: realOrder.subtotal,
                  shippingCost: realOrder.shippingCost
                };
              }
              return order;
            } catch (err) {
              console.error(`Errore fetch ordine ${order.orderId}:`, err);
              return order;
            }
          })
        );

        setOrders(enrichedOrders);
      } catch (err) {
        console.error('Errore nel caricamento ordini:', err);
      } finally {
        setLoading(false);
      }
    };
    loadOrders();
  }, [currentUser]);

  const getStatusBadge = (status) => {
    const config = {
      'pending_confirmation': { variant: 'warning', text: 'In attesa di conferma' },
      'confirmed': { variant: 'success', text: 'Confermato' },
      'processing': { variant: 'info', text: 'In elaborazione' },
      'shipped': { variant: 'primary', text: 'Spedito' },
      'delivered': { variant: 'success', text: 'Consegnato' },
      'cancelled': { variant: 'danger', text: 'Annullato' }
    };
    const c = config[status] || { variant: 'secondary', text: status };
    return <Badge bg={c.variant}>{c.text}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  return (
    <div className="py-2">
      <div className="mb-3 d-flex justify-content-start">
        <Button
          variant="dark"
          className="rounded-circle shadow-sm border-0"
          style={{ width: 44, height: 44, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => navigate('/profile')}
        >
          <i className="bi bi-arrow-left fs-4 text-white"></i>
        </Button>
      </div>

      <h4 className="mb-4 fw-bold">I miei ordini</h4>

      {orders.length === 0 ? (
        <div className="text-center py-5">
          <i className="bi bi-bag" style={{ fontSize: 80, color: '#e0e0e0' }}></i>
          <p className="text-muted mt-3">Nessun ordine effettuato</p>
          <Button variant="primary" className="rounded-pill" onClick={() => navigate('/')}>
            Vai allo shopping
          </Button>
        </div>
      ) : (
        orders.map((order, idx) => (
          <Card
            key={idx}
            className="mb-3 rounded-4 shop-card"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate(`/profile/orders/${order.shopId}/${order.orderId}`)}
          >
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <h6 className="fw-bold mb-1">Ordine #{String(order.orderNumber).padStart(6, '0')}</h6>
                  <p className="text-muted small mb-0">{order.shopName}</p>
                  <p className="text-muted small mb-0">{formatDate(order.createdAt)}</p>
                </div>
                {getStatusBadge(order.status)}
              </div>
              <div className="d-flex justify-content-between align-items-center mt-2">
                <span className="fw-bold text-dark">€{order.total.toFixed(2)}</span>
              </div>
            </Card.Body>
          </Card>
        ))
      )}
    </div>
  );
};

export default Orders;
